import React, { useState } from 'react';
import { Checkbox, FormControlLabel, Link, Typography } from '@mui/material';

import { EMAIL_REGEX, PropertySelectField } from '@paragon/client-tools';
import { userService } from '@paragon/data-connections';

import { LoginForm } from '../components/LoginForm';
import { LoginInput } from '../components/LoginInput';
import { LoginTypography } from '../components/LoginTypography';
import { PRIVACY_LINK, TERMS_LINK, useLoginFlash } from '../LoginScreen';

const unspecifiedIndustry = 'Pick an Industry';
const industryOptions = [
  unspecifiedIndustry,
  'Enterprise',
  'Component Manufacturer',
  'Engineering',
  'Architecture/Building Design',
  'Builder',
  'Software',
  'Plate Supplier',
  'Vendor/Supplier',
];

interface Props {
  readonly initialEmail?: string;
  readonly onSuccess: (email: string) => void;
}

export const SignUp = ({ initialEmail, onSuccess }: Props) => {
  const flash = useLoginFlash();

  const [email, setEmail] = useState<string>(initialEmail ?? '');
  const [emailError, setEmailError] = useState<boolean>(false);
  function validateEmail(emailToBeValidated: string) {
    const valid = emailToBeValidated !== '' && EMAIL_REGEX.test(emailToBeValidated);
    setEmailError(!valid);
    return valid;
  }

  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  function validateFirstName(firstNameToBeValidated: string) {
    const valid = firstNameToBeValidated !== '';
    setFirstNameError(!valid);
    return valid;
  }

  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  function validateLastName(lastNameToBeValidated: string) {
    const valid = lastNameToBeValidated !== '';
    setLastNameError(!valid);
    return valid;
  }

  const [company, setCompany] = useState<string>('');

  const [industry, setIndustry] = useState<string>(unspecifiedIndustry);

  const [understandsDisclaimer, setUnderstandsDisclaimer] = useState(false);

  async function onSubmit() {
    const validEmail = validateEmail(email);
    const validFirstName = validateFirstName(firstName);
    const validLastName = validateLastName(lastName);

    if (!validEmail || !validFirstName || !validLastName) return;

    const { success } = await userService.signup.start(
      email,
      firstName,
      lastName,
      company === '' ? null : company,
      industry === unspecifiedIndustry ? null : industry,
    );
    if (success) {
      onSuccess(email);
    } else {
      flash.set('error', 'Failed to initiate account creation. Contact help@paragontruss.com.');
    }
  }

  return (
    <LoginForm submitText="Sign Up" disabled={!understandsDisclaimer} onSubmit={onSubmit}>
      <LoginInput label="Email" autoFocus value={email} onChange={setEmail} error={emailError} />
      <LoginInput label="First Name" value={firstName} onChange={setFirstName} error={firstNameError} />
      <LoginInput label="Last Name" value={lastName} onChange={setLastName} error={lastNameError} />
      <LoginInput label="Company" required={false} value={company} onChange={setCompany} />
      <PropertySelectField
        sx={{ mt: 4 }}
        error={false}
        property="Industry"
        value={industry}
        options={industryOptions}
        submit={setIndustry}
      />

      <LoginTypography variant="body2" sx={{ marginTop: 2, marginBottom: 1, userSelect: 'none' }}>
        Paragon is not a professional engineering company. Paragon output is not to be used for any purposes unless
        sealed by a competent professional engineer.
      </LoginTypography>

      <FormControlLabel
        required
        control={
          <Checkbox checked={understandsDisclaimer} onChange={(_, checked) => setUnderstandsDisclaimer(checked)} />
        }
        label={
          <Typography variant="caption">
            I understand the above, and agree to Paragon&apos;s{' '}
            <Link href={TERMS_LINK} target="_blank" sx={{ fontWeight: 'bold' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href={PRIVACY_LINK} target="_blank" sx={{ fontWeight: 'bold' }}>
              Privacy Policy
            </Link>
            .
          </Typography>
        }
      />
    </LoginForm>
  );
};
